import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import api from '@/lib/api';

function useCountdown(endDate: string | null) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });

  useEffect(() => {
    if (!endDate) return;

    function calculate() {
      const diff = new Date(endDate!).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        expired: false,
      });
    }

    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  return timeLeft;
}

export default function CountdownTimer({ productId }: { productId?: string }) {
  const { t } = useTranslation('upsell');
  const { data: settingsData } = useSettings();
  const config = settingsData?.settings?.upsell_config?.countdown_timer;

  const { data } = useQuery({
    queryKey: ['countdown-timer', productId],
    queryFn: () =>
      api.get<{ timer: any }>(`/timers/active?${productId ? `product_id=${productId}` : ''}`),
    enabled: !!config?.enabled,
    refetchInterval: 60000,
  });

  const timer = data?.timer;
  const countdown = useCountdown(timer?.ends_at || null);

  if (!config?.enabled || !timer || countdown.expired || !timer.ends_at) return null;

  const isUrgent = countdown.days === 0 && countdown.hours === 0;

  return (
    <div className={`rounded-lg border p-4 ${isUrgent ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Clock className={`h-4 w-4 ${isUrgent ? 'text-red-600 animate-pulse' : 'text-amber-600'}`} aria-hidden="true" />
        <span className={`text-sm font-semibold ${isUrgent ? 'text-red-800' : 'text-amber-800'}`}>
          {timer.message || config.default_message || t('countdown.defaultMessage')}
        </span>
      </div>
      <div className="flex gap-3 justify-center" aria-label={t('countdown.ariaLabel')}>
        {countdown.days > 0 && (
          <TimeUnit value={countdown.days} label={t('countdown.units.days')} urgent={isUrgent} />
        )}
        <TimeUnit value={countdown.hours} label={t('countdown.units.hours')} urgent={isUrgent} />
        <TimeUnit value={countdown.minutes} label={t('countdown.units.minutes')} urgent={isUrgent} />
        <TimeUnit value={countdown.seconds} label={t('countdown.units.seconds')} urgent={isUrgent} />
      </div>
    </div>
  );
}

function TimeUnit({ value, label, urgent }: { value: number; label: string; urgent: boolean }) {
  return (
    <div className="text-center">
      <div className={`text-2xl font-bold font-mono ${urgent ? 'text-red-700' : 'text-amber-900'}`}>
        {String(value).padStart(2, '0')}
      </div>
      <div className={`text-[10px] uppercase tracking-wider ${urgent ? 'text-red-600' : 'text-amber-700'}`}>
        {label}
      </div>
    </div>
  );
}
